import { useCallback, useMemo, useRef, useState } from 'react'
import {
  SOCIAL_PLATFORMS,
  SOCIAL_POST_SOURCES,
  SOCIAL_SENTIMENTS,
  SOCIAL_TOPIC_METRICS,
  SOCIAL_TOPIC_POSTS_SORTS,
  SOCIAL_TOPIC_SEARCH_MAX_LENGTH,
  SocialMarketFilter,
  SocialPlatform,
  SocialPostSource,
  SocialSentiment,
  SocialTopicAuthorRef,
  SocialTopicCountsResponse,
  SocialTopicFilters,
  SocialTopicMetric,
  SocialTopicPostsSort,
  SocialWindow,
} from '@digital-twins/shared-types'
import { useSocialUniverseCounts } from './useSocialUniverse'

/**
 * A dimension a facet counts by; its own filter is left out of its counts.
 * `date` is the timeline: it reads the whole span, so the brush can move.
 */
export type SocialTopicFacet = 'channel' | 'brand' | 'sentiment' | 'source' | 'theme' | 'date'

export interface SocialTopicFilterState {
  platform: SocialPlatform | null
  brandId: string | null
  sentiment: SocialSentiment | null
  /** Who published or paid for the post, as the judge labelled it. */
  source: SocialPostSource | null
  /** A `SocialTopic.id` of the Topic, or SOCIAL_OTHER_THEME_ID. */
  themeId: string | null
  /** The search as typed; trimmed and capped at SOCIAL_TOPIC_SEARCH_MAX_LENGTH when sent. '' is no search. */
  q: string
  /** Whole UTC days (`socialTopicDayWindow`); null is the whole span. */
  window: SocialWindow | null
  /** Posts by one author (a top voice click). */
  author: SocialTopicAuthorRef | null
  /** Order of the boards' posts: Most reach (`views`) by default. Not a filter. */
  sort: SocialTopicPostsSort
  /** What every count reads: reach (views) by default, or posts. Not a filter. */
  metric: SocialTopicMetric
  /**
   * Market fit of every read (SOCIAL_MARKET_FILTERS); null is the server's
   * default, all markets. `inAndUnknown` is the market filter's on state. A
   * view choice like sort: Clear keeps it.
   */
  market: SocialMarketFilter | null
}

export const EMPTY_SOCIAL_TOPIC_FILTERS: SocialTopicFilterState = {
  platform: null,
  brandId: null,
  sentiment: null,
  source: null,
  themeId: null,
  q: '',
  window: null,
  author: null,
  sort: 'views',
  metric: 'reach',
  market: null,
}

/**
 * The page URL, when the filters live in it (the Topic page; the back
 * office Sheet keeps them local). Shaped like react-router's
 * `useSearchParams()`, so `{ searchParams, setSearchParams }` passes straight in.
 * Writes use the functional form, so they land on the live params, never on
 * a copy from the last render.
 */
export interface SocialTopicFiltersUrl {
  searchParams: URLSearchParams
  setSearchParams: (
    next: URLSearchParams | ((prev: URLSearchParams) => URLSearchParams),
    options?: { replace?: boolean },
  ) => void
}

export interface UseSocialTopicFiltersOptions {
  /** Absent: the state is local to the component. */
  url?: SocialTopicFiltersUrl
}

export interface UseSocialTopicFiltersResult {
  filters: SocialTopicFilterState
  /** True when any filter narrows the posts; sort and measure never count. */
  hasFilters: boolean
  setFilter: <K extends keyof SocialTopicFilterState>(
    key: K,
    value: SocialTopicFilterState[K],
  ) => void
  /** Clears every filter; keeps the sort, the measure and the market choice. */
  clear: () => void
  /**
   * The request filters for a facet, KPI or series counting by `excluding`:
   * every set filter but that dimension's own. Without `excluding`, every set
   * filter (what the posts and the Theme sections read). Search and author
   * apply to every facet; only `date` leaves the window out.
   */
  facetFilters: (excluding?: SocialTopicFacet) => SocialTopicFilters
}

const FACET_FILTER_KEY: Record<SocialTopicFacet, keyof SocialTopicFilterState> = {
  channel: 'platform',
  brand: 'brandId',
  sentiment: 'sentiment',
  source: 'source',
  theme: 'themeId',
  date: 'window',
}

/** The search as the API takes it: trimmed, capped; null for none. */
function requestSearch(q: string): string | null {
  const trimmed = q.trim().slice(0, SOCIAL_TOPIC_SEARCH_MAX_LENGTH).trim()
  return trimmed.length > 0 ? trimmed : null
}

/** Pure: the request filters of `state`, leaving out `excluding`'s own dimension. */
export function socialTopicFacetFilters(
  state: SocialTopicFilterState,
  excluding?: SocialTopicFacet,
): SocialTopicFilters {
  const skip = excluding ? FACET_FILTER_KEY[excluding] : null
  const q = requestSearch(state.q)
  return {
    ...(state.platform && skip !== 'platform' ? { platform: state.platform } : {}),
    ...(state.brandId && skip !== 'brandId' ? { brandId: state.brandId } : {}),
    ...(state.sentiment && skip !== 'sentiment' ? { sentiment: state.sentiment } : {}),
    ...(state.source && skip !== 'source' ? { source: state.source } : {}),
    ...(state.themeId && skip !== 'themeId' ? { themeId: state.themeId } : {}),
    ...(q ? { q } : {}),
    ...(state.window && skip !== 'window' ? { window: state.window } : {}),
    ...(state.author ? { author: state.author } : {}),
    ...(state.market ? { market: state.market } : {}),
  }
}

/** True when `state` narrows the posts (sort and measure are not filters). */
export function hasSocialTopicFilters(state: SocialTopicFilterState): boolean {
  return (
    state.platform != null ||
    state.brandId != null ||
    state.sentiment != null ||
    state.source != null ||
    state.themeId != null ||
    requestSearch(state.q) != null ||
    state.window != null ||
    state.author != null
  )
}

/**
 * A stable cache key part for request filters, every field in a fixed order
 * (search and author included), so two different selections never share a
 * cached response.
 */
export function socialTopicFiltersCacheKey(filters: SocialTopicFilters | undefined): string {
  return JSON.stringify([
    filters?.themeId ?? null,
    filters?.brandId ?? null,
    filters?.platform ?? null,
    filters?.sentiment ?? null,
    filters?.source ?? null,
    filters?.window?.from ?? null,
    filters?.window?.to ?? null,
    filters?.q ?? null,
    filters?.author?.platform ?? null,
    filters?.author?.handle ?? null,
    filters?.market ?? null,
  ])
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/

function isIsoDay(day: string): boolean {
  if (!ISO_DAY.test(day)) {
    return false
  }
  const ms = Date.parse(`${day}T00:00:00.000Z`)
  return !Number.isNaN(ms) && new Date(ms).toISOString().slice(0, 10) === day
}

/** The window of whole UTC days `first` to `last` (either order), as the brush and the URL set it. */
export function socialTopicDayWindow(first: string, last: string): SocialWindow {
  const [from, to] = first <= last ? [first, last] : [last, first]
  return { from: `${from}T00:00:00.000Z`, to: `${to}T23:59:59.999Z` }
}

/** The UTC days a window spans, `YYYY-MM-DD`; null for none or an unreadable one. */
export function socialTopicWindowDays(
  window: SocialWindow | null,
): { from: string; to: string } | null {
  if (!window) {
    return null
  }
  const from = Date.parse(window.from)
  const to = Date.parse(window.to)
  if (Number.isNaN(from) || Number.isNaN(to)) {
    return null
  }
  return { from: new Date(from).toISOString().slice(0, 10), to: new Date(to).toISOString().slice(0, 10) }
}

/** URL params the Topic filters own; every other param is left alone. */
const URL_KEYS = {
  platform: 'channel',
  brandId: 'brand',
  sentiment: 'sentiment',
  source: 'source',
  themeId: 'theme',
  q: 'q',
  from: 'from',
  to: 'to',
  author: 'author',
  sort: 'sort',
  metric: 'metric',
  market: 'market',
} as const

function includes<T extends string>(values: ReadonlyArray<T>, value: string | null): value is T {
  return value != null && (values as ReadonlyArray<string>).includes(value)
}

function nonEmpty(value: string | null): string | null {
  const trimmed = value?.trim() ?? ''
  return trimmed.length > 0 ? trimmed : null
}

function parseWindow(params: URLSearchParams): SocialWindow | null {
  const from = params.get(URL_KEYS.from)
  const to = params.get(URL_KEYS.to)
  if ((from != null && !isIsoDay(from)) || (to != null && !isIsoDay(to))) {
    return null
  }
  const first = from ?? to
  const last = to ?? from
  return first && last ? socialTopicDayWindow(first, last) : null
}

/** `platform:handle`, split at the first colon; the handle loses any leading `@`. */
function parseAuthor(value: string | null): SocialTopicAuthorRef | null {
  if (!value) {
    return null
  }
  const colon = value.indexOf(':')
  if (colon < 0) {
    return null
  }
  const platform = value.slice(0, colon)
  const handle = value.slice(colon + 1).trim().replace(/^@+/, '').trim()
  if (!includes(SOCIAL_PLATFORMS, platform) || handle.length === 0) {
    return null
  }
  return { platform, handle }
}

/** The filter state a Topic URL carries; a value outside the contract reads as unset. */
export function parseSocialTopicSearchParams(params: URLSearchParams): SocialTopicFilterState {
  const platform = params.get(URL_KEYS.platform)
  const sentiment = params.get(URL_KEYS.sentiment)
  const source = params.get(URL_KEYS.source)
  const sort = params.get(URL_KEYS.sort)
  const metric = params.get(URL_KEYS.metric)
  const market = params.get(URL_KEYS.market)
  const q = params.get(URL_KEYS.q) ?? ''
  return {
    platform: includes(SOCIAL_PLATFORMS, platform) ? platform : null,
    brandId: nonEmpty(params.get(URL_KEYS.brandId)),
    sentiment: includes(SOCIAL_SENTIMENTS, sentiment) ? sentiment : null,
    source: includes(SOCIAL_POST_SOURCES, source) ? source : null,
    themeId: nonEmpty(params.get(URL_KEYS.themeId)),
    q: requestSearch(q) ? q.slice(0, SOCIAL_TOPIC_SEARCH_MAX_LENGTH) : '',
    window: parseWindow(params),
    author: parseAuthor(params.get(URL_KEYS.author)),
    sort: includes(SOCIAL_TOPIC_POSTS_SORTS, sort) ? sort : EMPTY_SOCIAL_TOPIC_FILTERS.sort,
    metric: includes(SOCIAL_TOPIC_METRICS, metric) ? metric : EMPTY_SOCIAL_TOPIC_FILTERS.metric,
    // The market filter's on state is the only market view a control reaches; an old `market=all` link reads as unset, the same view.
    market: market === 'inAndUnknown' ? 'inAndUnknown' : null,
  }
}

/**
 * `params` with the Topic keys rewritten for `state` (defaults and unset
 * filters omitted, days for the window). A new object; other params keep
 * their place.
 */
export function writeSocialTopicSearchParams(
  params: URLSearchParams,
  state: SocialTopicFilterState,
): URLSearchParams {
  const next = new URLSearchParams(params)
  for (const key of Object.values(URL_KEYS)) {
    next.delete(key)
  }
  const days = socialTopicWindowDays(state.window)
  const entries: Array<[string, string | null]> = [
    [URL_KEYS.platform, state.platform],
    [URL_KEYS.brandId, state.brandId],
    [URL_KEYS.sentiment, state.sentiment],
    [URL_KEYS.source, state.source],
    [URL_KEYS.themeId, state.themeId],
    [URL_KEYS.q, requestSearch(state.q) ? state.q : null],
    [URL_KEYS.from, days?.from ?? null],
    [URL_KEYS.to, days?.to ?? null],
    [URL_KEYS.author, state.author ? `${state.author.platform}:${state.author.handle}` : null],
    [URL_KEYS.sort, state.sort !== EMPTY_SOCIAL_TOPIC_FILTERS.sort ? state.sort : null],
    [URL_KEYS.metric, state.metric !== EMPTY_SOCIAL_TOPIC_FILTERS.metric ? state.metric : null],
    [URL_KEYS.market, state.market],
  ]
  for (const [key, value] of entries) {
    if (value) {
      next.append(key, value)
    }
  }
  return next
}

/** Only the Topic keys of `params`, in a fixed order: what the parsed state depends on. */
function topicSearchKey(params: URLSearchParams): string {
  const own = new URLSearchParams()
  for (const key of Object.values(URL_KEYS)) {
    const value = params.get(key)
    if (value != null) {
      own.append(key, value)
    }
  }
  return own.toString()
}

function sameFilterValue(a: unknown, b: unknown): boolean {
  if (a === b) {
    return true
  }
  if (a == null || b == null || typeof a !== 'object' || typeof b !== 'object') {
    return false
  }
  return JSON.stringify(a) === JSON.stringify(b)
}

/**
 * The Topic view's one filter state (channel, brand, sentiment, source, theme,
 * search, window, author) plus its sort and measure. Every facet, KPI and
 * plot reads its request filters from `facetFilters`, so they all follow the
 * same selection. With `url`, the state lives in the page URL (shareable,
 * back and forward restore it); writes replace the history entry.
 */
export function useSocialTopicFilters(
  options: UseSocialTopicFiltersOptions = {},
): UseSocialTopicFiltersResult {
  const { url } = options
  const [localFilters, setLocalFilters] = useState<SocialTopicFilterState>(EMPTY_SOCIAL_TOPIC_FILTERS)

  const urlKey = url ? topicSearchKey(url.searchParams) : null
  const urlFilters = useMemo(
    () => (urlKey == null ? null : parseSocialTopicSearchParams(new URLSearchParams(urlKey))),
    [urlKey],
  )
  const filters = urlFilters ?? localFilters

  // The latest state, ahead of the next render, so two changes in one tick
  // (for example the brush setting a window and clearing the author) both land.
  // It follows `filters` only when they change, so a re-render before a
  // pending URL write lands keeps that write.
  const latestRef = useRef(filters)
  const seenFiltersRef = useRef(filters)
  if (seenFiltersRef.current !== filters) {
    seenFiltersRef.current = filters
    latestRef.current = filters
  }
  const urlRef = useRef(url)
  urlRef.current = url

  const apply = useCallback((next: SocialTopicFilterState) => {
    latestRef.current = next
    const target = urlRef.current
    if (target) {
      target.setSearchParams((prev) => writeSocialTopicSearchParams(prev, next), {
        replace: true,
      })
      return
    }
    setLocalFilters(next)
  }, [])

  const setFilter = useCallback(
    <K extends keyof SocialTopicFilterState>(key: K, value: SocialTopicFilterState[K]) => {
      const current = latestRef.current
      if (sameFilterValue(current[key], value)) {
        return
      }
      apply({ ...current, [key]: value })
    },
    [apply],
  )

  const clear = useCallback(() => {
    const current = latestRef.current
    if (!hasSocialTopicFilters(current) && current.q === '') {
      return
    }
    apply({
      ...EMPTY_SOCIAL_TOPIC_FILTERS,
      sort: current.sort,
      metric: current.metric,
      market: current.market,
    })
  }, [apply])

  const facetFilters = useCallback(
    (excluding?: SocialTopicFacet) => socialTopicFacetFilters(filters, excluding),
    [filters],
  )

  const hasFilters = hasSocialTopicFilters(filters)

  return useMemo(
    () => ({ filters, hasFilters, setFilter, clear, facetFilters }),
    [filters, hasFilters, setFilter, clear, facetFilters],
  )
}

interface UseSocialTopicFacetCountsOptions {
  /** Null makes no request. */
  token: string | null
  /** The brand facet is asked for only when the Topic names brands. */
  hasBrands: boolean
  topicFilters: Pick<UseSocialTopicFiltersResult, 'facetFilters'>
  refreshKey: string | number | null
}

interface UseSocialTopicFacetCountsResult {
  channelCounts: SocialTopicCountsResponse | null
  brandCounts: SocialTopicCountsResponse | null
  sentimentCounts: SocialTopicCountsResponse | null
  themeCounts: SocialTopicCountsResponse | null
  isLoadingThemes: boolean
}

/** The four facet counts of the filter row and the Theme sections, each without its own filter. */
export function useSocialTopicFacetCounts(
  options: UseSocialTopicFacetCountsOptions,
): UseSocialTopicFacetCountsResult {
  const { token, hasBrands, topicFilters, refreshKey } = options

  const { counts: channelCounts } = useSocialUniverseCounts(
    token,
    { groupBy: 'channel', filters: topicFilters.facetFilters('channel'), includeMarket: true },
    refreshKey,
  )
  const { counts: brandCounts } = useSocialUniverseCounts(
    hasBrands ? token : null,
    { groupBy: 'brand', filters: topicFilters.facetFilters('brand') },
    refreshKey,
  )
  const { counts: sentimentCounts } = useSocialUniverseCounts(
    token,
    { groupBy: 'sentiment', filters: topicFilters.facetFilters('sentiment') },
    refreshKey,
  )
  const { counts: themeCounts, isLoading: isLoadingThemes } = useSocialUniverseCounts(
    token,
    { groupBy: 'theme', filters: topicFilters.facetFilters('theme') },
    refreshKey,
  )

  return { channelCounts, brandCounts, sentimentCounts, themeCounts, isLoadingThemes }
}
